import { useReactiveVar } from '@apollo/client';
import {
  Box,
  Button,
  Divider,
  Modal,
  Paper,
  Skeleton,
  styled,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Typography
} from '@mui/material';
import Drawer from 'components/Drawer/Drawer';
import AddApiKey from 'components/Modal/variants/AddApiKey';
import dayjs, { Dayjs } from 'dayjs';
import { RestApiKey } from 'graphql/generated/graphqlTypes';
import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useRestApiKey from 'hooks/useRestApiKey/useRestApiKey';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import { upperCase, upperFirst } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { RestApiKeysState } from 'state/types';
import { authVar } from 'state/vars';
import Search from 'ui/molecules/Search/SearchMUI';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import { filterBySearchTerm } from 'utils/FilterBySearchTerm/FilterBySearchTerm.util';
import { createSettingsTabs } from '../settingsTabsConst';

const atlasRestApiDocsUrl = `${process.env.REACT_APP_ATLAS_REST_API_URL}/docs`;

export const StyledTypographyPasswordKeys = styled(Typography)(() => ({
  fontFamily: 'Courier New',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.15px'
}));

export const StyledPaper = styled(Paper)(() => ({
  backgroundColor: '#202020 !important',
  backgroundImage: 'none'
}));

export const createHeaders = (translation: Translation) =>
  [translation.name, translation.api_key_reference, translation.creation_date] as string[];

enum Order {
  asc = 'asc',
  desc = 'desc'
}

function descendingComparator<T>(aaa: T, bbb: T, orderBy: keyof T) {
  if (bbb[orderBy] < aaa[orderBy]) {
    return -1;
  }
  if (bbb[orderBy] > aaa[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<T>(order: Order, orderBy: keyof T): (aaa: T, bbb: T) => number {
  return order === 'desc'
    ? (aaa, bbb) => descendingComparator(aaa, bbb, orderBy)
    : (aaa, bbb) => -descendingComparator(aaa, bbb, orderBy);
}

const ApiKeys: React.FC = () => {
  const { translation, getLanguage, getLocaleForDayjs } = useTranslation();
  const { user } = useReactiveVar(authVar);
  const isEntraLess = user?.isEntraLess!;
  const settingsTabs = createSettingsTabs(translation, isEntraLess);
  const headerLeftTable = createHeaders(translation);
  const navigate = useNavigate();
  const [isAddKeyModalOpen, setIsAddKeyModalOpen] = useState(false);
  const { showDrawer } = useDrawer();
  const [filterStr, setFilterStr] = useState('');
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const { restApiKeys, loading } = useRestApiKey({ handleFetchError });
  const [order, setOrder] = useState<Order>(Order.asc);
  const [orderBy, setOrderBy] = useState<keyof RestApiKey>('description');

  const getFilterData = useCallback(
    (): RestApiKeysState =>
      filterBySearchTerm(restApiKeys, filterStr, (apiKey) => [
        apiKey.description,
        dayjs(apiKey.createdAt).locale(getLanguage()).format('hh:mm'),
        dayjs(apiKey.createdAt).locale(getLanguage()).format('DD MMM YYYY'),
        apiKey.apiKey
      ]),
    [filterStr, getLanguage, restApiKeys]
  );

  const handleOpenAddKeyModal = () => setIsAddKeyModalOpen(true);
  const handleCloseAddKeyModal = () => setIsAddKeyModalOpen(false);

  const handleRowClick = (id: string, name: string, reference: string, creationDate: string) => {
    showDrawer({
      type: 'editApiKey',
      contentValue: { name, reference, id, creationDate }
    });
  };

  const getTranslatedDate = (date: Dayjs) => date.locale(getLocaleForDayjs(getLanguage));

  const getFormattedDate = (date: Dayjs) => {
    const translatedDate = getTranslatedDate(date);
    return `${translatedDate.format('DD')} ${upperFirst(translatedDate.format('MMM'))} ${translatedDate.format(
      'YYYY'
    )}`;
  };
  const getFormattedHour = (date: Dayjs) => getTranslatedDate(date).format('HH:mm');

  const isTableVisible = restApiKeys.length > 0;
  const isNoResult = filterStr !== '' && getFilterData().length === 0;

  const handleRequestSort = (property: keyof RestApiKey) => {
    const isAsc = orderBy === property && order === Order.asc;
    setOrder(isAsc ? Order.desc : Order.asc);
    setOrderBy(property);
  };

  const sortedRows = useMemo(
    () => getFilterData().slice().sort(getComparator(order, orderBy)),
    [getFilterData, order, orderBy]
  );

  const getKeyByHeaderName = (header: string): keyof RestApiKey => {
    switch (header) {
      case translation.api_key_reference:
        return 'apiKey';
      case translation.creation_date:
        return 'createdAt';
      case translation.name:
      default:
        return 'description';
    }
  };

  return (
    <StyledPaper elevation={2} style={{ height: '100%' }}>
      <ComponentWrapper gap="2rem" padding="2.5rem" flexDirection="column">
        <Typography variant="h4" color="textPrimary">
          {translation.settings}
        </Typography>
        <ComponentWrapper>
          <Tabs indicatorColor="primary" textColor="primary" value={isEntraLess ? 0 : 1}>
            {settingsTabs.map((tab) => (
              <Tab label={tab.text} onClick={() => navigate(tab.location!)} />
            ))}
          </Tabs>
        </ComponentWrapper>
        <Typography variant="body2" color="textPrimary">
          {translation.api_keys_allow_you_to_manage_your_organization_via_the_atlas_admin_apis}
          <br />
          {translation.learn_more_about_the_atlas_api_from_the_documentation_here}{' '}
          <a
            id="atlas-rest-api-url"
            style={{ color: '#CC0' }}
            href={atlasRestApiDocsUrl}
            target="_blank"
            rel="noreferrer"
          >
            {atlasRestApiDocsUrl}
          </a>
          .
        </Typography>
        <Divider />
        <ComponentWrapper flexDirection="column" gap="1rem">
          <Typography variant="h6" color="textPrimary">
            {translation.api_keys}
          </Typography>
          <ComponentWrapper width="100%" justifyContent="space-between">
            <Search
              onChange={(event) => setFilterStr(event.target.value)}
              value={filterStr}
              style={{ width: '15rem' }}
            />
            <Button
              id="add-api-key-button"
              size="medium"
              color="primary"
              variant="contained"
              onClick={handleOpenAddKeyModal}
            >
              {translation.add_key}
            </Button>
          </ComponentWrapper>
        </ComponentWrapper>
        {isTableVisible && !loading && !isNoResult && (
          <TableContainer style={{ maxHeight: 'calc(100vh - 34rem)' }}>
            <Table stickyHeader style={{ borderCollapse: 'separate', borderSpacing: '0 0.25rem' }}>
              <TableHead>
                <TableRow>
                  {headerLeftTable.map((header) => (
                    <TableCell style={{ border: 'none', backgroundColor: '#202020' }}>
                      <TableSortLabel
                        active={orderBy === header}
                        direction={orderBy === getKeyByHeaderName(header) ? order : 'asc'}
                        onClick={() => handleRequestSort(getKeyByHeaderName(header))}
                      >
                        {header}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.map((apiKey, index) => (
                  <TableRow
                    onClick={() => handleRowClick(apiKey.id, apiKey.description, apiKey.apiKey, apiKey.createdAt)}
                    id={`row-${index}`}
                  >
                    <TableCell
                      sortDirection={orderBy === apiKey.id ? order : false}
                      width="34%"
                      style={{ border: 'none', padding: '0.375rem 1rem' }}
                    >
                      <Box>
                        <Typography variant="body2" id={`row-${index}-name`}>
                          {apiKey.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell width="33%" style={{ border: 'none', padding: '0.375rem 1rem' }}>
                      <Box>
                        <StyledTypographyPasswordKeys id={`row-${index}-api-key`}>
                          {apiKey.apiKey}
                        </StyledTypographyPasswordKeys>
                      </Box>
                    </TableCell>
                    <TableCell width="33%" style={{ border: 'none', padding: '0.375rem 1rem' }}>
                      <Box>
                        <Typography variant="body2" id={`row-${index}-time`}>
                          {getFormattedHour(dayjs(apiKey.createdAt))}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" id={`row-${index}-date`}>
                          {getFormattedDate(dayjs(apiKey.createdAt))}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {loading && !isTableVisible && !isNoResult && (
          <Table style={{ borderCollapse: 'separate', borderSpacing: '0 0.25rem' }}>
            <TableHead>
              <TableRow>
                {headerLeftTable.map((header) => (
                  <TableCell style={{ border: 'none', backgroundColor: '#202020' }}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }, (_, index) => (
                <TableRow key={index}>
                  <TableCell width="34%" style={{ border: 'none', padding: '0.375rem 1rem' }}>
                    <Skeleton width="100%" height={40} />
                  </TableCell>
                  <TableCell width="33%" style={{ border: 'none', padding: '0.375rem 1rem' }}>
                    <Skeleton width="100%" height={40} />
                  </TableCell>
                  <TableCell width="33%" style={{ border: 'none', padding: '0.375rem 1rem' }}>
                    <Skeleton width="100%" height={40} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {!loading && isTableVisible && isNoResult && (
          <ComponentWrapper
            flexDirection="column"
            padding="1rem 0 8rem 0"
            gap="1.5rem"
            alignItems="center"
            justifyContent="center"
            height="30rem"
          >
            <Typography color="textPrimary" variant="h4">
              {translation.no_results}
            </Typography>
            <Typography variant="body1" color="secondary" align="center">
              {translation.this_may_be_the_answer_you_re_looking_for}
              <br />
              {translation.if_not_try_changing_your_search_term}
            </Typography>
          </ComponentWrapper>
        )}
        {!isTableVisible && !loading && (
          <ComponentWrapper
            flexDirection="column"
            padding="1rem 0 8rem 0"
            gap="1.5rem"
            alignItems="center"
            justifyContent="center"
            height="30rem"
          >
            <Typography color="textPrimary" variant="h4">
              {translation.no_api_keys_added}
            </Typography>
            <Typography variant="body1" color="secondary" align="center">
              {translation.created_api_keys_will_appear_here}
              <br />
              {translation.would_you_like_to_create_a_api_key}
            </Typography>
            <Button onClick={handleOpenAddKeyModal} variant="contained" size="large" color="primary">
              {`+ ${upperCase(translation.add_key)}`}
            </Button>
          </ComponentWrapper>
        )}
        <Drawer />
        <Modal
          open={isAddKeyModalOpen}
          onClose={handleCloseAddKeyModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <AddApiKey handleClose={handleCloseAddKeyModal} />
        </Modal>
      </ComponentWrapper>
    </StyledPaper>
  );
};

export default ApiKeys;
